import { Limits } from './components/ChartComponent/chart/limits'
import { Ticks, createTicks, responsiveLabels } from './components/ChartComponent/chart/ticks'
import { FormatOptions, formatter } from './formatter'
export class TimeSeries {
  private squeezeFactor: number

  constructor(public values: { timestamp: Date; value: number | null }[]) {
    this.values = values.slice().sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
    this.squeezeFactor = 0.05
  }

  get length(): number {
    return this.values.length
  }

  get yLimits(): [number, number] {
    const values = this.valuesNonNull()
    return [Math.min(...values), Math.max(...values)]
  }

  get xLimits(): [number, number] {
    return [this.values[0].timestamp.getTime(), this.values[this.length - 1].timestamp.getTime()]
  }

  get xIndexLimits(): [number, number] {
    return [Math.min(...this.indices), Math.max(...this.indices)]
  }

  get limits(): Limits {
    return {
      x: this.xLimits,
      y: this.yLimits
    }
  }

  get indexLimits(): Limits {
    return {
      x: this.xIndexLimits,
      y: this.yLimits
    }
  }

  get indices(): number[] {
    const indices: number[] = [0]
    let index = 0
    for (let i = 1; i < this.length; i++) {
      if (this.values[i - 1].value === null || this.values[i].value === null)
        index += this.squeezeFactor
      else index++
      indices.push(index)
    }
    return indices
  }

  get timestamps(): number[] {
    return this.values.map((value) => value.timestamp.getTime())
  }

  get indexedPoints(): [number, number | null][] {
    return this.values.map((value, i) => [this.indices[i], value.value])
  }

  get points(): [number, number | null][] {
    return this.values.map((value) => [value.timestamp.getTime(), value.value])
  }

  getRange(start: Date, end: Date): TimeSeries {
    const values = this.values.filter((value) => value.timestamp >= start && value.timestamp <= end)
    return new TimeSeries(values)
  }

  getMissingDataRanges(): [number, number][] {
    const missingDataRanges: [number, number][] = []
    let start: number | null = null
    let end: number | null = null
    for (let i = 0; i < this.length; i++) {
      if (this.values[i].value === null) {
        if (start === null) start = i !== 0 ? i - 1 : i
        end = i !== this.length - 1 ? i + 1 : i
      } else {
        if (start !== null && end !== null && end - start > 0) {
          missingDataRanges.push([start, end])
          start = null
          end = null
        }
        start = null
      }
    }
    if (start !== null) {
      missingDataRanges.push([start, this.length - 1])
    }
    return missingDataRanges.map(([start, end]) => [this.indices[start], this.indices[end]])
  }

  valuesNonNull(): number[] {
    return this.values
      .filter((value) => value.value !== null)
      .map((value) => value.value) as number[]
  }

  removeNulls(): TimeSeries {
    return new TimeSeries(this.values.filter((value) => value.value !== null))
  }

  removeDuplicateNulls(): TimeSeries {
    const values = this.values.slice()
    let i = 0
    while (i < values.length - 1) {
      if (values[i].value === null && values[i + 1].value === null) {
        values.splice(i, 1)
      } else {
        i++
      }
    }
    return new TimeSeries(values)
  }

  getHorizontalTicks(
    viewportWidth: number,
    options: FormatOptions,
    locale: string
  ): Ticks<'complete'> {
    const ticks: Ticks<'complete'> = {
      values: this.indices,
      labels: this.points.map(([x, y]) => (y !== null ? formatter(x, locale, options) : ''))
    }
    return responsiveLabels(ticks, viewportWidth)
  }

  getVerticalTicks(
    dataFormat: FormatOptions,
    locale: string,
    dataTicks?: Ticks
  ): Ticks<'complete'> {
    return createTicks(dataTicks ?? 'auto', this.yLimits, this.valuesNonNull(), dataFormat, locale)
  }
}
